import React, { useState, useEffect } from "react";
import { useChangePropertyDetailsMutation, useGetPropertyDetailsQuery, useGetPropertyImageQuery } from "../../lib/apiSlice/property/propertySlice.tsx";
import { Link, useParams } from "react-router-dom";
import { useGetUnitDetailsQuery, useGetCorrectTerminologyQuery } from "../../lib/apiSlice/unit/unitSlice.tsx";
import EditPropertyModal from "./_editpropertyModal.jsx";
import { toast } from "react-toastify";


export default function TopSelectedPropertyDisplay({ propertyDetails,
    unitDetails,
    houseFilters,
    onHouseFilterChange }) {
    const { id } = useParams();
    const { data: propertyImage } = useGetPropertyImageQuery({ id: id });

    // Initialize with empty string
    const [backgroundImage, setBackgroundImage] = useState('');

    useEffect(() => {
        // If propertyImage is loaded and has at least one image, use it
        if (propertyImage && propertyImage.results && propertyImage.results.length > 0 && propertyImage.results[0].image) {
            setBackgroundImage(propertyImage.results[0].image);
        } else {
            // Otherwise, fallback to random stock image
            let storedImage = localStorage.getItem('backgroundImage');
            if (!storedImage) {
                let randomNumber = Math.floor(Math.random() * 10) + 1;
                storedImage = `https://kodinyumba.app/media/Stock/hd/Property/apartment${randomNumber}.jpg`;
                localStorage.setItem('backgroundImage', storedImage);
            }
            setBackgroundImage(storedImage);
        }
    }, [propertyImage]);

    // get the units
    const { data: correctTetminology } = useGetCorrectTerminologyQuery({ apartmentId: id });

    // Create a Set to store unique rent amounts
    const uniqueRentAmounts = new Set();

    // Filter houses array to get unique rent amounts chcek first if they have service charge
    const uniqueHouses = unitDetails?.results.filter((rentHouse) => {
        if (rentHouse.house_rent === 0) {
            // If rent is 0, check for service charge
            if (!uniqueRentAmounts.has(rentHouse.service_charge)) {
                uniqueRentAmounts.add(rentHouse.service_charge);
                return true;
            }
        } else {
            // If rent is not 0, use it as the unique identifier
            if (!uniqueRentAmounts.has(rentHouse.house_rent)) {
                uniqueRentAmounts.add(rentHouse.house_rent);
                return true;
            }
        }
        return false;
    });

    const uniqueHouseOccupancy = new Set();

    const uniquePaymentStatus = unitDetails?.results.filter((occupancyHouse) => {
        if (!uniqueHouseOccupancy.has(occupancyHouse.rent_payment_status)) {
            uniqueHouseOccupancy.add(occupancyHouse.rent_payment_status);
            return true;
        }
        return false;
    });

    // Get unique unit statuses (occupied/vacant)
    const uniquestatus = new Set();
    const uniqueStatuses = unitDetails?.results.filter((statusHouse) => {
        // Determine if unit is occupied (has a tenant) or vacant
        const status = statusHouse.tenant_id ? "occupied" : "vacant";
        if (!uniquestatus.has(status)) {
            uniquestatus.add(status);
            return true;
        }
        return false;
    });

    // Modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({
        name: "",
        location: "",
        about: "",
        property_type: "",
    });

    useEffect(() => {
        if (propertyDetails?.results?.[0]) {
            setEditForm({
                name: propertyDetails.results[0].name || "",
                location: propertyDetails.results[0].location || "",
                about: propertyDetails.results[0].about || "",
                property_type: propertyDetails.results[0].property_type || "",
            });
        }
    }, [propertyDetails]);

    const handleEditChange = (e) => {
        setEditForm({ ...editForm, [e.target.name]: e.target.value });
    };
    const [editPropertyDetails] = useChangePropertyDetailsMutation()

    const handleSave = async () => {
        try {
            await editPropertyDetails({ id, propertyData: editForm }).unwrap();
            setShowEditModal(false);
            toast.success('Succefully saved the changes')

        }
        catch (err) {
            console.log(err);
            setShowEditModal(false);
            toast.error('Sorry an error occured while saving the changes')
        }
    };

    const handleSearchChange = (e) => {
        onHouseFilterChange(prev => ({ ...prev, search: e.target.value }));
    };

    const handleRentAmountChange = (e) => {
        onHouseFilterChange(prev => ({ ...prev, rentAmount: e.target.value }));
    };

    const handlePaymentStatusChange = (e) => {
        onHouseFilterChange(prev => ({ ...prev, paymentStatus: e.target.value }));
    };

    const handlestatusChange = (e) => {
        onHouseFilterChange(prev => ({ ...prev, status: e.target.value }));
    };

    return (
        <>
            <div className="relative w-full">
                {/* Top right buttons */}
                <div className="top-4 right-4 z-20 absolute flex gap-2">
                    <badge className={`bg-${propertyDetails?.results?.[0]?.property_type === "comercial" ? "green" : propertyDetails?.results?.[0]?.property_type === "residential" ? "gray" : "yellow"}-500 text-white font-semibold capitalize p-2 rounded shadow`}>
                        {propertyDetails?.results?.[0]?.property_type === "both" ? "Residential and Comercial" : propertyDetails?.results?.[0]?.property_type}
                    </badge>

                    <Link to={`/selected-property-image/${id}`} className="text-decoration-none" >
                        <button
                            className="bg-purple-400 hover:bg-purple-500 shadow px-4 py-2 rounded font-semibold text-white transition"
                        >
                            Gallery
                        </button>
                    </Link>

                    <button
                        className="bg-blue-600 hover:bg-blue-700 shadow px-4 py-2 rounded font-semibold text-white transition"
                        onClick={() => setShowEditModal(true)}
                    >
                        Edit
                    </button>
                </div>

                {/* Background Image */}
                <div
                    className="bg-cover bg-center rounded-md w-full h-64 md:h-68 lg:h-72"
                    style={{
                        backgroundImage: `url('${backgroundImage}')`,
                        filter: "brightness(0.85)",
                    }}
                ></div>
                {/* Overlay for darkening */}
                <div className="z-10 absolute inset-0 bg-black/20 w-full h-72 md:h-76 lg:h-80"></div>
                {/* Title */}
                <div className="top-1/2 left-1/2 absolute flex flex-col justify-center items-center w-full"
                    style={{ transform: "translate(-50%, -60%)" }}>
                    <span className="drop-shadow-lg mb-3 font-normal text-md text-white md:text-lg text-center uppercase tracking-wide"
                        style={{ textShadow: "0 4px 16px rgba(0,0,0,0.5)" }}>
                        {propertyDetails?.results?.[0]?.location}
                    </span>

                    <h1 className="drop-shadow-lg font-normal text-white text-3xl md:text-5xl text-center uppercase tracking-wide"
                        style={{ textShadow: "0 4px 16px rgba(0,0,0,0.5)" }}>
                        {propertyDetails?.results?.[0]?.name}
                    </h1>
                </div>

                {/* Search/Filter Bar */}
                <div
                    className="bottom-0 left-1/2 absolute flex px-8 w-full"
                    style={{
                        transform: "translate(-50%, 50%)",
                        zIndex: 20,
                        pointerEvents: "none", // allow click-through except for the bar itself
                    }}
                >
                    <div
                        className="flex md:flex-row flex-col items-stretch md:items-center gap-3 md:gap-6 space-x-10 bg-white shadow-md -mt-4 px-3 md:px-8 py-4 md:py-6 rounded-sm min-w-full max-w-7xl"
                        style={{
                            pointerEvents: "auto", // enable interaction
                        }}
                    >
                        {/* Search */}
                        <input
                            type="text"
                            placeholder="Search Unit"
                            value={houseFilters.search}
                            onChange={handleSearchChange}
    
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        {/* Location Dropdown */}
                        <select 
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={houseFilters.rentAmount}
                        onChange={handleRentAmountChange}
                    >
                        <option value="">All Amounts</option>
                        {Array.isArray(uniqueHouses) && uniqueHouses.map((rentHouse) => (
                            rentHouse.house_rent === 0 ? (
                                <option key={rentHouse.house_id} value={rentHouse.service_charge}>
                                    Ksh. {rentHouse.service_charge?.toLocaleString('en-us')}
                                </option>
                            ) : (
                                <option key={rentHouse.house_id} value={rentHouse.house_rent}>
                                    Ksh. {rentHouse.house_rent?.toLocaleString('en-us')}
                                </option>
                            )
                        ))}
                    </select>

                         {/* Payment Status Dropdown */}
                    <select 
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={houseFilters.paymentStatus}
                        onChange={handlePaymentStatusChange}
                    >
                        <option value="">All Statuses</option>
                        {Array.isArray(uniquePaymentStatus) && uniquePaymentStatus.map((rentHouse) => (
                            rentHouse.rent_payment_status === "None" || rentHouse.rent_payment_status === null ? null : (
                                <option className='capitalize' key={rentHouse.house_id} value={rentHouse.rent_payment_status}>
                                    {rentHouse.rent_payment_status}
                                </option>
                            )
                        ))}
                    </select>

                    {/* Unit Status Dropdown (Occupied/Vacant) */}
                    <select 
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={houseFilters.status || ""}
                        onChange={handlestatusChange}
                    >
                        <option value="">All Units</option>
                        <option value="occupied">Occupied</option>
                        <option value="vacant">Vacant</option>
                    </select>
                    </div>
                </div>
            </div>
            {/* Spacer for search bar overlap */}
            <div className="h-24 md:h-16"></div>

            {/* edit modal */}
            <EditPropertyModal
                show={showEditModal}
                onClose={() => setShowEditModal(false)}
                form={editForm}
                onChange={handleEditChange}
                onSave={handleSave}
            />
        </>
    );
}
